import {
  Button,
  Spinner,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
} from '@chakra-ui/react'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import PageWrapper from '../../../../../../components/Pagewrapper'
import {
  coreApi,
  useGetLatestRescoreRunQuery,
  usePreviewRescoreAllComplianceInvestorsMutation,
  usePreviewRescoreStaleComplianceInvestorsMutation,
  useRescoreAllComplianceInvestorsMutation,
  useRescoreStaleComplianceInvestorsMutation,
} from '../../../../../../redux/services/coreApi'
import type {
  ComplianceCaseType,
  ComplianceState,
  InvestorPreviewRowDto,
  PreservedDecisionDto,
  RescoreFreezeReason,
  RescorePreviewDto,
  RescoreBatchResultDto,
  RescoreRunState,
  RescoreRunStatusDto,
  RescoreScope,
  RestrictionCountDto,
  RiskTier,
} from '../../../../../../types/coreApi-types'

// The RTK mutation trigger for a void-argument endpoint: callable with no args,
// returning a thenable exposing .unwrap(). Structurally compatible with the
// generated hook triggers, which carry extra members we don't need here.
type RescoreTrigger<T> = () => { unwrap: () => Promise<T> }

const RuleSetsScreen: React.FC = () => {
  const [previewStale, { isLoading: isPreviewingStale }] =
    usePreviewRescoreStaleComplianceInvestorsMutation()
  const [rescoreStale, { isLoading: isStartingStale }] =
    useRescoreStaleComplianceInvestorsMutation()
  const [previewAll, { isLoading: isPreviewingAll }] =
    usePreviewRescoreAllComplianceInvestorsMutation()
  const [rescoreAll, { isLoading: isStartingAll }] =
    useRescoreAllComplianceInvestorsMutation()

  const dispatch = useDispatch()
  const toast = useToast()

  // The one background bulk-rescore run (either scope). A commit only STARTS
  // the run (202) — the book takes minutes to re-score, longer than any single
  // request survives — so the outcome is polled off this status while the run
  // is in flight. Committing invalidates the RescoreRun tag, which refetches
  // this immediately; visiting the page mid-run picks the run straight up.
  // Polling while mounted (an in-memory read on the API) rather than only
  // while Running — it needs no self-referential wiring and also surfaces a
  // run another admin triggered while this page sat open.
  const { data: latestRun } = useGetLatestRescoreRunQuery(undefined, {
    pollingInterval: 3000,
  })
  const run = latestRun?.run ?? null

  // Toast exactly once when the in-flight run lands — and only then refresh
  // the broad user caches (the commit POST itself changed nothing yet).
  const prevStateRef = useRef<RescoreRunState | null>(null)
  useEffect(() => {
    const state = run?.state ?? null
    const prev = prevStateRef.current
    prevStateRef.current = state
    if (prev !== 'Running' || state === 'Running' || state === null) return
    if (state === 'Completed' && run?.result) {
      toast({
        title: 'Re-score finished',
        description: `${run.result.rescored} of ${run.result.total} customers re-scored, ${run.result.failed} failed.`,
        status: run.result.failed === 0 ? 'success' : 'warning',
        position: 'top-right',
        duration: 8000,
      })
      dispatch(coreApi.util.invalidateTags(['Users', 'User']))
    } else if (state === 'Failed') {
      toast({
        title: 'Re-score failed',
        description:
          run?.error ?? 'Check the API logs for the underlying error.',
        status: 'error',
        position: 'top-right',
      })
    }
  }, [run, dispatch, toast])

  return (
    <PageWrapper
      title="Rule sets"
      description="Bulk re-score customers under the active rule set — either only those left on an older version (a normal rule-set migration), or every customer regardless of version (after a scoring fix that also affects customers already on the active version)."
    >
      <RescoreSection
        title="Rule-set migration (stale only)"
        totalLabel="Stale customers"
        commitLabel="Re-score stale customers"
        confirmTitle="Re-score stale customers?"
        description={
          <>
            <p className="text-description">
              Re-scores only customers whose stored answers reference a rule-set
              version <strong>other than</strong> the active one. Run this after
              deploying a new MLRO-approved rule set (e.g. v7.6 → v7.8).
            </p>
            <p className="text-description">
              Always <strong>preview</strong> first — it reports exactly what
              would change (risk-tier and state transitions, EDD cases that would
              open, restrictions) <strong>without writing anything</strong>. The
              commit is locked until you have previewed.
            </p>
          </>
        }
        confirmBody={
          <>
            Committing adds a new <code>RuleSetMigration</code>-typed assessment
            per stale customer (the audit trail stays append-only), recomputes
            tier and state, applies/lifts restrictions, opens the previewed
            cases, and notifies the CRM. Failures on individual customers do not
            affect others.
            <br />
            <br />
            Run this only after MLRO has approved the new rule-set version and the
            corresponding YAML has been deployed.
          </>
        }
        scope="StaleByVersion"
        run={run}
        runPreview={previewStale}
        isPreviewing={isPreviewingStale}
        runCommit={rescoreStale}
        isStartingCommit={isStartingStale}
      />

      <div className="mt-8">
        <RescoreSection
          title="Re-score ALL customers (ignore version)"
          totalLabel="Customers"
          commitLabel="Re-score all customers"
          confirmTitle="Re-score every customer?"
          description={
            <>
              <p className="text-description">
                Re-scores <strong>every</strong> customer with questionnaire
                answers, <strong>regardless of rule-set version</strong>. Use
                this after a scoring fix that changes results for customers who
                are already on the active version — where a stale migration would
                skip them because there is no version drift to detect.
              </p>
              <p className="text-description">
                Always <strong>preview</strong> first. This touches the whole
                scored population, so review the would-change report carefully
                before committing.
              </p>
            </>
          }
          confirmBody={
            <>
              This re-scores <strong>every customer</strong> with answers, not
              just those on an older version. Each gets a new{' '}
              <code>RuleSetMigration</code> assessment (append-only audit trail),
              recomputed tier and state, restrictions applied/lifted, previewed
              cases opened, and a CRM notification. Failures on individual
              customers do not affect others.
            </>
          }
          scope="All"
          run={run}
          runPreview={previewAll}
          isPreviewing={isPreviewingAll}
          runCommit={rescoreAll}
          isStartingCommit={isStartingAll}
        />
      </div>
    </PageWrapper>
  )
}

// One scoped bulk-rescore workflow: preview (dry-run) → gated commit → the
// background run's live status and, when it lands, its report. Rendered once
// per scope (stale / all); the two differ only in copy and which endpoints
// they call. `run` is the one background run (either scope) — a section shows
// it only when it is its own scope's, but while ANY run is in flight both
// sections keep their commit locked.
const RescoreSection: React.FC<{
  title: string
  totalLabel: string
  commitLabel: string
  confirmTitle: string
  description: React.ReactNode
  confirmBody: React.ReactNode
  scope: RescoreScope
  run: RescoreRunStatusDto | null
  runPreview: RescoreTrigger<RescorePreviewDto>
  isPreviewing: boolean
  runCommit: RescoreTrigger<RescoreRunStatusDto>
  isStartingCommit: boolean
}> = ({
  title,
  totalLabel,
  commitLabel,
  confirmTitle,
  description,
  confirmBody,
  scope,
  run,
  runPreview,
  isPreviewing,
  runCommit,
  isStartingCommit,
}) => {
  const [preview, setPreview] = useState<RescorePreviewDto | null>(null)
  const confirm = useDisclosure()
  const toast = useToast()

  const runInFlight = run?.state === 'Running'
  const sectionRun = run && run.scope === scope ? run : null

  // Gate: the commit is only enabled once a preview has been run this session,
  // and never while a bulk run (of either scope) is already in flight.
  const canCommit = preview !== null && !isPreviewing && !runInFlight

  const onPreview = async () => {
    try {
      const result = await runPreview().unwrap()
      setPreview(result)
      toast({
        title: 'Preview ready',
        description: `${result.changed} of ${result.total} customers would change.`,
        status: 'success',
        position: 'top-right',
        duration: 6000,
      })
    } catch {
      toast({
        title: 'Preview failed',
        description: 'Check the API logs for the underlying error.',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  const onCommit = async () => {
    confirm.onClose()
    try {
      await runCommit().unwrap()
      // Force a fresh preview before another run. The commit only STARTED the
      // run — the screen-level poll surfaces its progress and outcome.
      setPreview(null)
      toast({
        title: 'Re-score started',
        description:
          'Running in the background — the result appears below when it finishes. Leaving this page does not stop it.',
        status: 'info',
        position: 'top-right',
        duration: 8000,
      })
    } catch {
      toast({
        title: 'Could not start the re-score',
        description: 'Check the API logs for the underlying error.',
        status: 'error',
        position: 'top-right',
      })
    }
  }

  return (
    <>
      <div className="card gap-3">
        <h2 className="text-lg font-semi mb-2">{title}</h2>
        {description}
        <div className="flex gap-2">
          <Button
            variant="outline"
            colorScheme="blue"
            isLoading={isPreviewing}
            onClick={onPreview}
          >
            Preview changes
          </Button>
          <Button
            colorScheme="blue"
            isLoading={isStartingCommit || Boolean(sectionRun && runInFlight)}
            // Keep the label (and the button's accessible name) while loading.
            loadingText={commitLabel}
            isDisabled={!canCommit}
            onClick={confirm.onOpen}
            title={
              canCommit
                ? undefined
                : runInFlight
                  ? 'A bulk re-score is already running'
                  : 'Run a preview first'
            }
          >
            {commitLabel}
          </Button>
        </div>
      </div>

      {preview && <PreviewReport preview={preview} totalLabel={totalLabel} />}

      {sectionRun?.state === 'Running' && <RunInProgress run={sectionRun} />}

      {sectionRun?.state === 'Failed' && <RunFailed run={sectionRun} />}

      {sectionRun?.state === 'Completed' && sectionRun.result && (
        <LastRunReport result={sectionRun.result} />
      )}

      <Modal isOpen={confirm.isOpen} onClose={confirm.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{confirmTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            You previewed{' '}
            <strong>
              {preview?.changed ?? 0} change(s) across {preview?.total ?? 0}{' '}
              customer(s)
            </strong>
            . {confirmBody}
          </ModalBody>
          <ModalFooter className="gap-2">
            <Button variant="ghost" onClick={confirm.onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={onCommit}>
              {commitLabel}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

const RunInProgress: React.FC<{ run: RescoreRunStatusDto }> = ({ run }) => (
  <div className="card gap-3 mt-4">
    <div className="flex items-center gap-3">
      <Spinner size="sm" />
      <h2 className="text-lg font-semi">Re-scoring in progress</h2>
    </div>
    <p className="text-description">
      Started at {new Date(run.startedAtUtc).toLocaleTimeString()} — running on
      the server, one customer at a time. Leaving this page does not stop it;
      the result appears here when it finishes.
    </p>
  </div>
)

const RunFailed: React.FC<{ run: RescoreRunStatusDto }> = ({ run }) => (
  <div className="card gap-3 mt-4">
    <h2 className="text-lg font-semi mb-2">Last run failed</h2>
    <p className="text-description">
      {run.error ?? 'Check the API logs for the underlying error.'}
    </p>
  </div>
)

const LastRunReport: React.FC<{ result: RescoreBatchResultDto }> = ({
  result,
}) => (
  <div className="card gap-3 mt-4">
    <h2 className="text-lg font-semi mb-2">
      Last run — active rule set {result.activeRuleSetVersion}
    </h2>
    <SummaryRow label="Total" value={result.total} />
    <SummaryRow label="Re-scored" value={result.rescored} />
    <SummaryRow label="Failed" value={result.failed} />

    {result.failures.length > 0 && (
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-default-small">
          <thead>
            <tr className="text-left text-description border-b border-solid border-brand-light-card-border">
              <th className="py-2 pr-4 font-semi">Investor</th>
              <th className="py-2 font-semi">Reason</th>
            </tr>
          </thead>
          <tbody>
            {result.failures.map(f => (
              <tr
                key={f.investorId}
                className="border-b border-solid border-brand-light-card-border last:border-b-0"
              >
                <td className="py-2 pr-4 font-mono text-xs">{f.investorId}</td>
                <td className="py-2">{f.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)

const PreviewReport: React.FC<{
  preview: RescorePreviewDto
  totalLabel: string
}> = ({ preview, totalLabel }) => (
  <div className="card gap-3 mt-4">
    <h2 className="text-lg font-semi mb-2">
      Preview — active rule set {preview.activeRuleSetVersion} (nothing written)
    </h2>
    <SummaryRow label={totalLabel} value={preview.total} />
    <SummaryRow label="Would change" value={preview.changed} />
    <SummaryRow label="Unchanged" value={preview.unchanged} />
    {preview.errors > 0 && (
      <SummaryRow label="Could not preview" value={preview.errors} />
    )}

    <Section title="Risk-tier transitions">
      {preview.tierTransitions.length === 0 ? (
        <EmptyNote>No risk-tier changes.</EmptyNote>
      ) : (
        preview.tierTransitions.map((t, i) => (
          <CountRow
            key={i}
            label={`${tierLabel(t.from)} → ${tierLabel(t.to)}`}
            value={t.count}
          />
        ))
      )}
    </Section>

    <Section title="State transitions">
      {preview.stateTransitions.length === 0 ? (
        <EmptyNote>No state changes.</EmptyNote>
      ) : (
        preview.stateTransitions.map((s, i) => (
          <CountRow
            key={i}
            label={`${stateLabel(s.from)} → ${stateLabel(s.to)}`}
            value={s.count}
          />
        ))
      )}
    </Section>

    <Section title="Cases that would open">
      {preview.casesToOpen.length === 0 ? (
        <EmptyNote>No new cases.</EmptyNote>
      ) : (
        preview.casesToOpen.map((c, i) => (
          <CountRow
            key={i}
            label={`${caseTypeLabel(c.caseType)} (${c.assignedRole.toUpperCase()})`}
            value={c.count}
          />
        ))
      )}
    </Section>

    {(preview.caseDrivers?.length ?? 0) > 0 && (
      <Section title="What is driving those cases">
        <p className="text-description text-default-small mb-2">
          The matrix rule or EDD reason behind each case above. A single rule
          accounting for most of the count usually means a rule change landing on
          a whole segment, rather than many unrelated customers.
        </p>
        {preview.caseDrivers.map((d, i) => (
          <CountRow key={i} label={d.driver} value={d.count} />
        ))}
      </Section>
    )}

    {(preview.restrictionsToApply.length > 0 ||
      preview.restrictionsToLift.length > 0) && (
      <Section title="Restrictions">
        {preview.restrictionsToApply.map((r: RestrictionCountDto, i) => (
          <CountRow
            key={`apply-${i}`}
            label={`Apply — ${restrictionLabel(r.kind)}`}
            value={r.count}
          />
        ))}
        {preview.restrictionsToLift.map((r: RestrictionCountDto, i) => (
          <CountRow
            key={`lift-${i}`}
            label={`Lift — ${restrictionLabel(r.kind)}`}
            value={r.count}
          />
        ))}
        <div className="mt-2 pt-2 border-t border-solid border-brand-light-card-border">
          <p className="text-default-small text-description mb-1">
            On already-approved customers — the bulk run leaves these to the CO
            (ADR-0012 §9b), so a plausibility migration should show none here; any
            apply is an EDD/HIGH escalation, never the withheld plausibility gate.
          </p>
          {(preview.restrictionsToApplyApproved ?? []).length === 0 &&
          (preview.restrictionsToLiftApproved ?? []).length === 0 ? (
            <CountRow
              label="Restriction changes on approved customers"
              value={0}
            />
          ) : (
            <>
              {(preview.restrictionsToApplyApproved ?? []).map(
                (r: RestrictionCountDto, i) => (
                  <CountRow
                    key={`aa-${i}`}
                    label={`Apply — ${restrictionLabel(r.kind)}`}
                    value={r.count}
                  />
                ),
              )}
              {(preview.restrictionsToLiftApproved ?? []).map(
                (r: RestrictionCountDto, i) => (
                  <CountRow
                    key={`la-${i}`}
                    label={`Lift — ${restrictionLabel(r.kind)}`}
                    value={r.count}
                  />
                ),
              )}
            </>
          )}
        </div>
      </Section>
    )}

    {preview.changedInvestors.length > 0 && (
      <div className="overflow-x-auto mt-3">
        <table className="w-full text-default-small">
          <thead>
            <tr className="text-left text-description border-b border-solid border-brand-light-card-border">
              <th className="py-2 pr-4 font-semi">Customer</th>
              <th className="py-2 pr-4 font-semi">Tier</th>
              <th className="py-2 pr-4 font-semi">State</th>
              <th className="py-2 pr-4 font-semi">Restrictions</th>
              <th className="py-2 pr-4 font-semi">Case opened</th>
              <th className="py-2 font-semi">Drivers</th>
            </tr>
          </thead>
          <tbody>
            {preview.changedInvestors.map((r: InvestorPreviewRowDto) => (
              <tr
                key={r.investorId}
                className="border-b border-solid border-brand-light-card-border last:border-b-0"
              >
                <td className="py-2 pr-4">
                  {r.name ?? (
                    <span className="font-mono text-xs">{r.investorId}</span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {r.oldTier !== r.newTier ? (
                    <span>
                      {tierLabel(r.oldTier)} → <strong>{tierLabel(r.newTier)}</strong>
                    </span>
                  ) : (
                    tierLabel(r.newTier)
                  )}
                </td>
                <td className="py-2 pr-4">
                  {r.oldState !== r.newState ? (
                    <span>
                      {stateLabel(r.oldState)} →{' '}
                      <strong>{stateLabel(r.newState)}</strong>
                    </span>
                  ) : (
                    stateLabel(r.newState)
                  )}
                  {r.rejectReasons?.length > 0 && (
                    <span className="block text-xs text-description">
                      {r.rejectReasons.map(rejectReasonLabel).join(', ')}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {r.restrictionsToApply.length === 0 &&
                  r.restrictionsToLift.length === 0 ? (
                    <span className="text-description">—</span>
                  ) : (
                    <>
                      {r.restrictionsToApply.map((k, i) => (
                        <span key={`a-${i}`} className="block text-xs">
                          + {restrictionLabel(k)}
                        </span>
                      ))}
                      {r.restrictionsToLift.map((k, i) => (
                        <span
                          key={`l-${i}`}
                          className="block text-xs text-description"
                        >
                          − {restrictionLabel(k)}
                        </span>
                      ))}
                    </>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {r.caseToOpen ? (
                    `${caseTypeLabel(r.caseToOpen)} (${r.caseAssignedRole?.toUpperCase()})`
                  ) : r.manualReviewSuppressed ? (
                    <span className="text-description">
                      suppressed (already cleared)
                    </span>
                  ) : (
                    <span className="text-description">—</span>
                  )}
                </td>
                <td className="py-2">
                  {r.caseDrivers.length > 0 ? (
                    <span className="text-xs">{r.caseDrivers.join(', ')}</span>
                  ) : (
                    <span className="text-description">—</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {(preview.preservedDecisions?.length ?? 0) > 0 && (
      <div className="overflow-x-auto mt-3">
        <h3 className="font-semi mb-1">Decisions preserved (not changed)</h3>
        <p className="text-description text-default-small mb-2">
          These customers keep their current tier/state because an MLRO set it
          manually, they are rejected, or a review of theirs has not been decided
          — still queued, or parked as deferred. A re-score never releases someone
          on a review nobody settled. It records a new assessment for audit but
          changes nothing live. Check the ones where the engine now disagrees:
          those are the reviews to finish by hand.
        </p>
        <table className="w-full text-default-small">
          <thead>
            <tr className="text-left text-description border-b border-solid border-brand-light-card-border">
              <th className="py-2 pr-4 font-semi">Customer</th>
              <th className="py-2 pr-4 font-semi">Kept</th>
              <th className="py-2 pr-4 font-semi">Reason</th>
              <th className="py-2 font-semi">Engine would score</th>
            </tr>
          </thead>
          <tbody>
            {preview.preservedDecisions.map((r: PreservedDecisionDto) => (
              <tr
                key={r.investorId}
                className="border-b border-solid border-brand-light-card-border last:border-b-0"
              >
                <td className="py-2 pr-4">
                  {r.name ?? (
                    <span className="font-mono text-xs">{r.investorId}</span>
                  )}
                </td>
                <td className="py-2 pr-4">
                  {stateLabel(r.currentState)}
                  {r.currentTier ? ` · ${tierLabel(r.currentTier)}` : ''}
                </td>
                <td className="py-2 pr-4">{freezeReasonLabel(r.freezeReason)}</td>
                <td className="py-2">
                  {r.recomputedRejected ? 'Reject' : tierLabel(r.recomputedTier)}
                  {r.recomputedRejectReasons?.length > 0 && (
                    <span className="block text-xs text-description">
                      {r.recomputedRejectReasons.map(rejectReasonLabel).join(', ')}
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {preview.failures.length > 0 && (
      <div className="overflow-x-auto mt-3">
        <h3 className="font-semi mb-1">Could not preview</h3>
        <table className="w-full text-default-small">
          <tbody>
            {preview.failures.map(f => (
              <tr
                key={f.investorId}
                className="border-b border-solid border-brand-light-card-border last:border-b-0"
              >
                <td className="py-2 pr-4 font-mono text-xs">{f.investorId}</td>
                <td className="py-2">{f.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
)

const tierLabel = (tier?: RiskTier | null): string => tier ?? 'None'

const stateLabel = (state: ComplianceState): string =>
  state.replace(/([a-z])([A-Z])/g, '$1 $2')

const caseTypeLabel = (type: ComplianceCaseType): string =>
  type.replace(/([a-z])([A-Z])/g, '$1 $2')

// Known reject codes get a short human label; the code stays visible so it
// remains the canonical audit reference. Unknown codes fall through as-is.
const REJECT_REASON_LABELS: Record<string, string> = {
  R1_FATF_BLACK_RES: 'FATF black-listed residence',
  R1_CRYPTO_BAN: 'Crypto banned in residence',
  R1_RESTRICTED_RES: 'Restricted residence',
  R1_FATF_BLACK_NAT: 'FATF black-listed nationality',
  R2_SANCTIONS_MATCH: 'Sanctions match',
  R3_THIRD_PARTY: 'Third-party funds',
  R4_IDENTITY_INCONSISTENCY: 'Identity not verified',
  R5_SOF_UNVERIFIABLE: 'Source of funds unverifiable',
  R6_UNDER_18: 'Under 18',
}

const rejectReasonLabel = (code: string): string => {
  const label = REJECT_REASON_LABELS[code]
  return label ? `${label} (${code})` : code
}

const freezeReasonLabel = (reason: RescoreFreezeReason): string => {
  switch (reason) {
    case 'ManualTierOverride':
      return 'Manual tier override'
    case 'Rejected':
      return 'Rejected (terminal)'
    case 'UndecidedReview':
      return 'Review not decided'
    default:
      return reason
  }
}

const restrictionLabel = (kind: string): string =>
  kind.replace(/([a-z])([A-Z])/g, '$1 $2')

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="mt-2">
    <h3 className="font-semi mb-1">{title}</h3>
    {children}
  </div>
)

const EmptyNote: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-description text-default-small">{children}</p>
)

const CountRow: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between">
    <span>{label}</span>
    <span className="font-semi">{value}</span>
  </div>
)

const SummaryRow: React.FC<{ label: string; value: number }> = ({
  label,
  value,
}) => (
  <div className="flex items-center justify-between">
    <span className="text-description">{label}</span>
    <span className="font-semi">{value}</span>
  </div>
)

export default RuleSetsScreen
